import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Document, DocumentCategory, DocumentType } from 'src/app/services/documents/documents';
import { Observable, Subscription } from 'rxjs';
import { FormType } from 'src/app/common/enumerations/enumerations';
import { ReviewCycle } from '../reviews/reviews.models';
import { DocumentsRefresh } from 'src/app/services/documents/store/documents.action';
import { MatDialog } from '@angular/material/dialog';
import { RedirectDialogComponent } from './redirect-dialog/redirect-dialog.component';
import { ReviewsRefresh } from '../reviews/store/reviews/reviews.action';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { AlertService, ToastrStyle } from 'src/app/services/alert/alert.service';
import { SCFMastersRefresh } from '../scf-v2/store/scf-master/scf-masters.action';
import { FormsRefresh } from 'src/app/controls/form-table/store/forms.action';
import { ChecklistsRefresh } from '../checklists/store/checklists/checklists.action';
import { AbsiRefresh } from '../absi/store/absi/absi.action';
import { KEnableRefresh } from '../kenable/store/ke/kenable.action';
import { RSSWARefresh } from '../rsswa/store/rsswa/rsswa.action';

@Injectable({
  providedIn: 'root'
})
export class RedirectService {

  document?: Document | null;
  documents?: Document[];
  documentsFiltered?: Document[];
  documents$!: Observable<Document[]>;
  documentsSubs!: Subscription;

  documentTypes?: DocumentType[];
  documentTypesFiltered?: DocumentType[];
  documentTypes$!: Observable<DocumentType[]>;
  documentTypesSubs!: Subscription;

  reviewCycles?: ReviewCycle[];
  reviewCycles$!: Observable<ReviewCycle[]>;
  reviewCyclesSubs!: Subscription;
  openDocuments: OpenDocument[] = [];

  previousUrl: string | null = null;
  currentUrl: string | null = null;

  mouseX?: string;
  mouseY?: string;
  private documentsRefreshPromise?: Promise<void>;

  constructor(
    private store: Store,
    private router: Router,
    private dialog: MatDialog,
    private auth: AuthenticationService,
    private alert: AlertService
  ) {

  }

  openDialog() {
    const dialogRef = this.dialog.open(RedirectDialogComponent, { width: '300px', height: '300px' });
    dialogRef.afterClosed();
  }

  async openDocument(document: Document) {
    if (document) {
      this.document = document;
      let route = '';

      this.documentTypes$ = this.store.select(state => state.DocumentType.data);
      this.documentTypesSubs = this.documentTypes$.subscribe(async data => {
        if (data?.length) {
          this.documentTypes = data;

          switch (document.category) {
            case DocumentCategory.Form:
              document.documentType = this.documentTypes?.find(t => t.id == document.type);
              route = document.documentType?.code?.toLowerCase().trim() ?? '';
              this.store.dispatch(new SCFMastersRefresh());
              this.store.dispatch(new RSSWARefresh());
              this.store.dispatch(new FormsRefresh());
              break;

            case DocumentCategory.Checklist:
              const documentType = this.documentTypes?.find(t => t.type == document.type);
              if (documentType) {
                route = 'checklist-exec/' + documentType?.id;
                this.store.dispatch(new ChecklistsRefresh());
              }
              break;

            case DocumentCategory.Review:
              // Wait for the asynchronous getReviewRoute() to complete
              route = await this.getReviewRoute(document.serialNo);
              this.store.dispatch(new ReviewsRefresh());
              break;

            case DocumentCategory.ChecklistTemplate:
              document.documentType = this.documentTypes?.find(t => t.id == document.type);
              if (document.documentType && document.formID) {
                localStorage.setItem('checklistTemplateID', document.formID.toString());
                route = 'checklist-builder/' + document.documentType.id;
              }
              break;

            case DocumentCategory.ABSITemplate:
              route = 'absi-builder';
              this.store.dispatch(new AbsiRefresh());
              break;

            case DocumentCategory.KETemplate:
              route = 'resources-new/1';
              this.store.dispatch(new KEnableRefresh());
              break;

          }

          // Navigate only after determining the route
          if (this.auth.hasValidSession())
            this.router.navigate([route]);
          else {
            localStorage.setItem('redirect-route', this.router.url);
            const AUTH_MESSAGE_HTML = `
            <div class="ascc-auth-message">
              <h4>Sign in required</h4>
              <h5>You must be logged in to <b>ASCC</b> to continue to <b>${document.serialNo}</b></h4>

                <a class="btn login-btn">Sign in to ASCC</a>

              <p class="hint">If you’re redirected back here after signing in, please try again or contact support.</p>
            </div>
            `;
            this.alert.banner(AUTH_MESSAGE_HTML, '', ToastrStyle.Big);
            this.router.navigate(['login']);
          }
        }

      });
    }
  }


  getReviewRoute(serialNo?: string): Promise<string> {
    return new Promise((resolve) => {
      this.reviewCycles$ = this.store.select(state => state.ReviewCycles.data);
      this.reviewCyclesSubs = this.reviewCycles$.subscribe(data => {
        if (data?.length) {
          this.reviewCycles = data;
          const reviewCycle = this.reviewCycles.find(c => c.serialNo == serialNo);
          if (reviewCycle?.reviewBoard) {
            resolve(`reviews/${reviewCycle.reviewBoard.type}`);
          } else {
            const review = reviewCycle?.reviews?.[0];
            const boardType = review?.reviewCycle?.reviewBoard?.type;
            if (boardType) {
              resolve(`reviews/${boardType}`);
            } else {
              resolve('');
            }
          }
        } else {
          setTimeout(() => {
            resolve(this.getReviewRoute(serialNo)); // Retry
          }, 500);
        }
      });
    });
  }


  loadDocuments() {
    if (this.documentsSubs && !this.documentsSubs.closed) {
      return;
    }

    this.documents$ = this.store.select(state => state.Documents.data);
    this.documentsSubs = this.documents$.subscribe(data => {
      if (data?.length) {
        this.documents = data;
      }
    });
  }

  loadDocumentTypes() {
    if (this.documentTypesSubs && !this.documentTypesSubs.closed) {
      return;
    }

    this.documentTypes$ = this.store.select(state => state.DocumentType.data);
    this.documentTypesSubs = this.documentTypes$.subscribe(data => {
      if (data?.length) {
        this.documentTypes = data;
      }
    });
  }

  async getDocument(
    type: FormType,
    retries: number = 0,
    maxRetries: number = 3
  ): Promise<Document | undefined | null> {
    if (
      this.document?.type == type ||
      (this.document?.category == DocumentCategory.Checklist &&
        this.document.type == this.documentTypes?.find(t => t.id == type)?.type)
    ) {
      return this.document;
    } else {
      const openDocument = this.openDocuments.find(o => o.type == type);
      if (openDocument?.id) {
        this.loadDocuments();
        this.loadDocumentTypes();
        const document = this.findDocument(openDocument.id, openDocument.type);
        if (document) return document;

        if (retries < maxRetries) {
          if (retries == 0) {
            await this.refreshDocumentsOnce();
          }
          await new Promise(resolve => setTimeout(resolve, 1000)); // Delay for 1 second
          return this.getDocument(type, retries + 1, maxRetries); // Increment retry counter
        } else {
          console.error(`Failed to retrieve document after ${maxRetries} attempts.`);
          return null; // Abort after maxRetries
        }
      }
    }
    return null;
  }

  /**
   * Refreshes the document catalog once for a redirect lookup and shares that request across callers.
   * Prevents each retry from starting another `/Documents` request while still giving the store one chance to load.
   */
  private async refreshDocumentsOnce(): Promise<void> {
    if (!this.documentsRefreshPromise) {
      // Cache the dispatch promise so concurrent redirect lookups join the same refresh.
      this.documentsRefreshPromise = this.store.dispatch(new DocumentsRefresh()).toPromise()
        .then(() => undefined)
        .finally(() => this.documentsRefreshPromise = undefined);
    }

    await this.documentsRefreshPromise;
  }


  findDocument(id: number, type: number) {
    const document = this.documents?.find(d => d.formID == id &&
      (d.type == type ||
        d.category == DocumentCategory.Checklist && d.type == this.documentTypes?.find(t => t.id == type)?.type)
    );
    return document;
  }
}

interface OpenDocument {
  id?: number;
  type: FormType;
}
